import { useEffect, useState, useCallback } from "react";
import { useAppSelecter } from "../../../../hooks/storeHooks";
import { ObjectValues } from "../../../../pages/login";

/**
 * Custom hook to manage "copied" data from localStorage.
 * Reacts to Redux refetchCopy state.
 */
const useCopiedData = () => {
  const [copiedData, setCopiedDataState] = useState<any[]>([]);

  // 👇 Listen to global Redux flag for refetch trigger
  const refetchCopy = useAppSelecter(
    (state: ObjectValues) => state.userSlice?.refetchCopy
  );

  /** Decode and parse from base64 localStorage */
  const fetchCopiedData = useCallback(() => {
    try {
      const stored = localStorage.getItem("copied");
      if (stored) {
        const decoded = JSON.parse(decodeURIComponent(escape(atob(stored))));
        if (Array.isArray(decoded)) {
          setCopiedDataState(decoded);
        } else {
          setCopiedDataState([]);
        }
      } else {
        setCopiedDataState([]);
      }
    } catch (err) {
      console.warn("Error reading copied data:", err);
      setCopiedDataState([]);
    }
  }, []);

  /** Encode and save to localStorage */
  const updateCopiedData = useCallback((newData: any[]) => {
    try {
      const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(newData))));
      localStorage.setItem("copied", encoded);
      setCopiedDataState(newData);
    } catch (err) {
      console.warn("Error saving copied data:", err);
    }
  }, []);

  /** Clear copied data */
  const clearCopiedData = useCallback(() => {
    localStorage.removeItem("copied");
    setCopiedDataState([]);
  }, []);

  /** Initial load */
  useEffect(() => {
    fetchCopiedData();
  }, [fetchCopiedData]);

  /** Re-fetch when Redux refetchCopy changes */
  useEffect(() => {
    if (refetchCopy) {
      fetchCopiedData();
    }
  }, [refetchCopy, fetchCopiedData]);

  return {
    copiedData,
    setCopiedData: updateCopiedData,
    clearCopiedData,
    refetchCopy
  };
};

export default useCopiedData;
